import type { FC } from "react";
import { useCallback } from "react";

import { useStoreLocatorContext } from "@locators/components/shared/hooks";
import classNames from "classnames";

import { SearchDefaultIcon } from "@jti/brand-icons/search-default";
import { UiTargetIcon } from "@jti/brand-icons/ui-target";
import { Button } from "@jti/core-components/components/Button";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import { StoreLocatorFilters } from "../StoreLocatorFilters";

import styles from "./StoreLocatorSearchBar.module.scss";

export const StoreLocatorSearchBar: FC = () => {
  const { __ } = useTranslations();
  const {
    inputRef,
    isFiltersOpened,
    filtersEnabled,
    isSearchResultPending = false,
    isSearchVisible,
    getUserMarkerPosition,
    handleInputSearchClick,
  } = useStoreLocatorContext();

  const handleCurrentLocationClick = useCallback(() => {
    if (inputRef.current?.value) {
      inputRef.current.value = "";
    }
    getUserMarkerPosition();
  }, [getUserMarkerPosition]);

  return (
    <div
      role="search"
      data-testid="storeLocatorSearchBar"
      className={classNames(styles.barContainer, {
        [styles.barContainerOpened]: isFiltersOpened,
        [styles.horizontalAlignment]: !isSearchVisible,
      })}
    >
      <div className={classNames(styles.barHeaderContainer)} data-testid="text" data-anchor="anchor-text">
        <h2 className={styles.barHeaderText} data-testid="PolicyPageInnerText">
          {__("storelocator.search_bar.header")}
        </h2>
        {!isSearchVisible && (
          <button
            className={classNames(styles.currentLocationButton, {
              [styles.currentLocationButtonWhite]: !isSearchVisible,
            })}
            onClick={handleCurrentLocationClick}
            aria-label={__("storelocator.use_current_location")}
          >
            <UiTargetIcon height="20px" width="20px" aria-hidden="true" />
            <span>
              <span>{__("storelocator.use_current_location")}</span>
            </span>
          </button>
        )}
      </div>
      {isSearchVisible && (
        <>
          <div className={styles.inputWrapper}>
            <div className={styles.searchInputLabel}>
              <SearchDefaultIcon className={styles.placeholderIcon} width="20px" height="20px" />
              <input
                ref={inputRef}
                id="storeSearchInput"
                className={classNames([styles.searchInput, "pac-target-input"])}
                type="text"
                name="searchInput"
                placeholder={__("storelocator.placeholder.search")}
                aria-label={__("storelocator.placeholder.search")}
              />
              <button
                className={classNames([styles.currentLocationButton, styles.currentLocationButtonInput])}
                onClick={handleCurrentLocationClick}
                aria-label={__("storelocator.use_current_location")}
              >
                <UiTargetIcon height="20px" width="20px" aria-hidden="true" />
              </button>
            </div>
            <Button
              variant="primary"
              onClick={handleInputSearchClick}
              pending={isSearchResultPending}
              aria-label={__("storelocator.button.label")}
            >
              {__("storelocator.button.label")}
            </Button>
          </div>
        </>
      )}
      {filtersEnabled && (
        <div
          className={classNames(styles.searchBottomContainer, {
            [styles.withoutPaddings]: !isSearchVisible,
          })}
        >
          <StoreLocatorFilters />
        </div>
      )}
    </div>
  );
};
